import React, { useState, useEffect, useRef } from 'react';
import { SkeletonDashboard } from '@/components/loading/Skeleton';
import Header, { HeaderRef } from './components/Header';
import CustomerOverview from './components/advisor/CustomerOverview';
import FunctionButtons from './components/advisor/FunctionButtons';
import MarketOverview from './components/advisor/MarketOverview';
import ChatSection from './components/advisor/ChatSection';
import { qryCopilotValueApi, getCopilotValueByTaskIdApi } from '@/api/copilot';
import { EXTERNAL_USER_ID } from '@/config/env';
import Charts, { ChartsRef } from './components/charts';
import { ChatProvider } from '@/contexts/ChatContext';
import { initWecom } from '@/utils/wecom';
import { WECOM_CORP_ID } from '@/config/env';

const HomePage: React.FC = () => {
  const customerOverviewRef = useRef<any>(null);
  const headerRef = useRef<HeaderRef>(null);
  const [taskId, setTaskId] = useState<string>('');
  // 客户画像agent  (reqType: 1)
  const [userProfile, setUserProfile] = useState({});
  // 客户画像额外信息 (reqType: 2)
  const [extendedProfile, setExtendedProfile] = useState({});
  // 持仓诊断agent (reqType: 3)
  const [portfolioDiagnosis, setPortfolioDiagnosis] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const hasFetched = useRef(false);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const chartsViewportRef = useRef<HTMLDivElement | null>(null);
  const chartsRef = useRef<ChartsRef | null>(null);
  console.log(window.location.href, 'window.location.href');

  const initWecomFn = async () => {
    const success = await initWecom({
      corpId: WECOM_CORP_ID
    });
    console.log(success, 'success');
  };

  useEffect(() => {
    initWecomFn();
  }, []);

  // 获取Copilot数据
  const fetchCopilotData = async () => {
    try {
      setLoading(true);
      const response = await qryCopilotValueApi({
        externalUserId: EXTERNAL_USER_ID,
        reqType: 0
      });
      console.log(response);
      if (response) {
        customerOverviewRef.current.init();
        setTaskId(response);

        const pUseAll = [1, 2, 3].map(reqType => {
          return getCopilotValueByTaskIdApi({
            externalUserId: EXTERNAL_USER_ID,
            reqType,
            taskId: response
          });
        });

        Promise.allSettled(pUseAll).then(results => {
          console.log('获取Copilot数据成功:', results);
          results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              console.log(result.value);
              if (index === 0) {
                setUserProfile(result.value as any);
              } else if (index === 1) {
                setExtendedProfile(result.value as any);
              } else if (index === 2) {
                setPortfolioDiagnosis(result.value as any);
              }
            }
          });
        });
      }
    } catch (error) {
      console.error('获取Copilot数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    console.log('HomePage refresh 被调用');
    fetchCopilotData();

    // 如果当前在图表 Tab，也刷新 Charts 组件
    if (activeTab === 1 && chartsRef.current) {
      console.log('同时刷新 Charts 组件');
      chartsRef.current.refresh();
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    console.log('HomePage 组件挂载');
    // 防止在 StrictMode 下重复调用
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCopilotData();
    }
  }, []);

  const handleHeadClick = (_: string, index: number) => {
    console.log('handleHeadClick', index);
    setActiveTab(index);
  };

  const scrollDown = () => {
    console.log('scrollDown 开始执行，当前 activeTab:', activeTab);

    // 根据当前激活的 tab 滚动对应的容器
    if (activeTab === 0) {
      // 投顾应用区域
      if (viewportRef.current) {
        console.log('滚动投顾应用区域', viewportRef.current);
        console.log('scrollHeight:', viewportRef.current.scrollHeight);
        console.log('clientHeight:', viewportRef.current.clientHeight);

        viewportRef.current.scrollTo({
          top: viewportRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    } else if (activeTab === 1) {
      // 图表区域
      if (chartsViewportRef.current) {
        console.log('滚动图表区域', chartsViewportRef.current);
        console.log('scrollHeight:', chartsViewportRef.current.scrollHeight);
        console.log('clientHeight:', chartsViewportRef.current.clientHeight);

        chartsViewportRef.current.scrollTo({
          top: chartsViewportRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }

    // 备用方案：尝试查找所有可见的滚动容器
    setTimeout(() => {
      const visibleScrollContainers = document.querySelectorAll(
        '.overflow-y-auto:not([style*="display: none"])'
      );
      console.log('找到可见的滚动容器数量:', visibleScrollContainers.length);

      visibleScrollContainers.forEach((container, index) => {
        console.log(`滚动容器 ${index}:`, container);
        console.log(`容器 ${index} scrollHeight:`, container.scrollHeight);
        console.log(`容器 ${index} clientHeight:`, container.clientHeight);

        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    }, 100);
  };

  const handleChatClick = (index: number) => {
    console.log('handleChatClick', headerRef.current, index);
    setActiveTab(index);
    // 安全检查，确保 headerRef.current 存在
    if (headerRef.current) {
      headerRef.current.setActiveTab(index);
    }
  };

  return (
    <ChatProvider>
      <div className="relative mx-auto min-h-screen w-full bg-purple-100 bg-gradient-to-b from-blue-50 via-blue-50 to-purple-100">
        {/* 头部导航 */}
        <Header
          ref={headerRef}
          refresh={refresh}
          handleHeadClick={handleHeadClick}
          isShowBack={false}
        />

        {/* 投顾应用区域 */}
        {activeTab === 0 && (
          <div
            className="relative h-[calc(100vh-84px-50px)] overflow-y-auto"
            style={{ display: activeTab === 0 ? 'block' : 'none' }}
            ref={viewportRef}
          >
            {loading && (
              <div className="absolute left-0 top-0 z-[111111] flex h-full w-full items-center justify-center">
                <SkeletonDashboard className="absolute left-0 top-0 w-full" />
              </div>
            )}
            <div
              className={`space-y-4 px-4 pb-40 ${
                loading ? 'opacity-0' : 'opacity-100'
              } transition-opacity duration-300`}
            >
              {/* 客户概览卡片 */}
              <CustomerOverview
                ref={customerOverviewRef}
                taskId={taskId}
                userProfile={userProfile}
                extendedProfile={extendedProfile}
                portfolioDiagnosis={portfolioDiagnosis}
              />

              {/* 功能按钮区域 */}
              <FunctionButtons taskId={taskId} />

              {/* 市场动态 */}
              <MarketOverview taskId={taskId} />
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div
            className="relative h-[calc(100vh-84px-50px)] space-y-4 overflow-y-auto px-4 pb-40"
            style={{ display: activeTab === 1 ? 'block' : 'none' }}
            ref={chartsViewportRef}
          >
            <Charts ref={chartsRef} />
          </div>
        )}

        {/* 底部聊天区域 */}
        <ChatSection scrollDown={scrollDown} setActiveTab={handleChatClick} />
      </div>
    </ChatProvider>
  );
};

export default HomePage;
